import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useI18n } from '../context/I18nContext';

function clamp(value, min, max) {
  let next = value;
  if (Number.isFinite(min)) next = Math.max(min, next);
  if (Number.isFinite(max)) next = Math.min(max, next);
  return next;
}

export default function CustomNumberInput({
  value,
  onChange,
  min,
  max,
  step = 1,
  fixedDecimals,
  minIntegerDigits = 0,
  placeholder = '',
  disabled = false,
  commitOnChange = false,
  className = '',
  inputClassName = '',
  id,
  name,
  required = false,
}) {
  const { t } = useI18n();
  const formatValue = useCallback(
    (nextValue) => {
      const numeric = Number(nextValue);
      if (!Number.isFinite(numeric)) return String(nextValue ?? '');

      let formatted =
        Number.isInteger(fixedDecimals) && fixedDecimals >= 0
          ? numeric.toFixed(fixedDecimals)
          : String(numeric);

      if (Number.isInteger(minIntegerDigits) && minIntegerDigits > 0) {
        const [integerPart, decimalPart] = formatted.split('.');
        const isNegative = integerPart.startsWith('-');
        const absoluteInteger = isNegative ? integerPart.slice(1) : integerPart;
        const paddedInteger = absoluteInteger.padStart(minIntegerDigits, '0');
        formatted = `${isNegative ? '-' : ''}${paddedInteger}${
          decimalPart !== undefined ? `.${decimalPart}` : ''
        }`;
      }

      return formatted;
    },
    [fixedDecimals, minIntegerDigits],
  );

  const [text, setText] = useState(formatValue(value ?? ''));

  useEffect(() => {
    setText(formatValue(value ?? ''));
  }, [formatValue, value]);

  function handleBlur() {
    const numeric = Number(text);
    if (!Number.isFinite(numeric)) {
      setText(formatValue(value ?? ''));
      return;
    }
    const normalized = clamp(numeric, min, max);
    onChange?.(normalized);
    setText(formatValue(normalized));
  }

  function handleChange(nextText) {
    setText(nextText);
    if (!commitOnChange) return;

    const numeric = Number(nextText);
    if (!Number.isFinite(numeric)) return;

    const normalized = clamp(numeric, min, max);
    onChange?.(normalized);
  }

  function nudge(direction) {
    const current = Number(text);
    const fallback = Number.isFinite(Number(value)) ? Number(value) : 0;
    const base = Number.isFinite(current) ? current : fallback;
    const next = clamp(base + direction * Number(step || 1), min, max);
    onChange?.(next);
    setText(formatValue(next));
  }

  return (
    <div className={`relative ${className}`}>
      <input
        id={id}
        name={name}
        required={required}
        type="text"
        inputMode={Number(step) % 1 === 0 ? 'numeric' : 'decimal'}
        value={text}
        onChange={(event) => handleChange(event.target.value)}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            nudge(1);
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            nudge(-1);
          }
          if (event.key === 'Enter') {
            handleBlur();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full bg-transparent border-0 ${inputClassName}`}
      />
      <div className="absolute inset-y-0 right-2 flex flex-col justify-center gap-1">
        <button
          type="button"
          tabIndex={-1}
          onClick={() => nudge(1)}
          disabled={disabled}
          className="flex h-3 w-3 items-center justify-center rounded-t-md bg-[var(--dash-border)] dark:bg-[var(--dash-surface)] text-[var(--dash-text-muted)] leading-none transition-all hover:bg-[var(--dash-primary)] hover:text-[var(--dash-text-white)] dark:hover:bg-[var(--dash-primary)] dark:hover:text-[var(--dash-text-white)] disabled:opacity-50"
          aria-label={t('components.customNumberInput.increaseValue', 'Increase value')}
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          onClick={() => nudge(-1)}
          disabled={disabled}
          className="flex h-3 w-3 items-center justify-center rounded-b-md bg-[var(--dash-border)] dark:bg-[var(--dash-surface)] text-[var(--dash-text-muted)] leading-none transition-all hover:bg-[var(--dash-primary)] hover:text-[var(--dash-text-white)] dark:hover:bg-[var(--dash-primary)] dark:hover:text-[var(--dash-text-white)] disabled:opacity-50"
          aria-label={t('components.customNumberInput.decreaseValue', 'Decrease value')}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
